/**
 * Starblast demo — client entry point.
 *
 * Order of operations:
 *   1. Build the PlayServ SDK client from .env values.
 *   2. Mount the DOM session overlay and pipe `client.session` transitions
 *      into it (recovering / banned states render above the canvas).
 *   3. Hand control to Phaser; BootScene awaits `client.ready()` before
 *      switching to ArenaScene.
 */

import Phaser from 'phaser';

import { buildPlayServClient } from './playserv';
import { mountSessionOverlay, type OverlayState } from './ui/sessionOverlay';
import { BootScene } from './scenes/BootScene';
import { ArenaScene } from './scenes/ArenaScene';
import { getMatchConfig } from './data/matchConfig';
import { getPlayer } from './data/player';

const client = buildPlayServClient();

const overlay = mountSessionOverlay();
client.session.wait('Update', (event) => {
  // eslint-disable-next-line no-console
  console.info(`[session] state → ${event.state}`, event.details ?? '');
  // SDK has 5 states; overlay knows 4. `awaitingClientSettings` sits between
  // handshake and full readiness — surface it as "recovering" to the user.
  const overlayState: OverlayState =
    event.state === 'awaitingClientSettings' ? 'recovering' : event.state;
  overlay.setState(overlayState, event.details as { reason?: unknown } | undefined);
});

// PSV-1911 QA — true full-screen: the internal height stays 720 (HUD
// metrics assume it) but the WIDTH follows the screen aspect at boot, so
// FIT fills the whole screen instead of letterboxing. HUD anchors read
// cam.width at creation, so a non-1280 width lays out correctly. Clamped
// so extreme aspects can't hide the HUD or reveal half the arena.
//
// Measurement: on touch devices NEITHER innerWidth/innerHeight NOR
// visualViewport can be trusted at module-eval time — iOS webviews still
// report the pre-layout 980px layout viewport (seen live twice: 4:3
// canvas on a 2.16:1 phone). `screen.width/height` are the physical CSS
// dimensions and stable before layout; on iOS they are
// orientation-independent, so orient the pair by the media query. The
// game is effectively fullscreen on phones, so screen aspect == viewport
// aspect. Desktop windows are not fullscreen — there the live window
// metrics are correct and needed.
const GAME_HEIGHT = 720;
function bootAspect(): number {
  const coarse =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches;
  if (coarse && window.screen.width > 0 && window.screen.height > 0) {
    const long = Math.max(window.screen.width, window.screen.height);
    const short = Math.min(window.screen.width, window.screen.height);
    const portrait =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(orientation: portrait)').matches;
    return portrait ? short / long : long / short;
  }
  const vv = window.visualViewport;
  const w = vv !== null && vv !== undefined && vv.width > 0 ? vv.width : window.innerWidth;
  const h = vv !== null && vv !== undefined && vv.height > 0 ? vv.height : window.innerHeight;
  return h > 0 ? w / h : 16 / 9;
}
const GAME_WIDTH = Math.round(
  GAME_HEIGHT * Math.min(21 / 9, Math.max(4 / 3, bootAspect())),
);

const game = new Phaser.Game({
  type: Phaser.AUTO,
  width: GAME_WIDTH,
  height: GAME_HEIGHT,
  scale: {
    mode: Phaser.Scale.FIT,
    autoCenter: Phaser.Scale.CENTER_BOTH,
    width: GAME_WIDTH,
    height: GAME_HEIGHT,
  },
  parent: 'game',
  backgroundColor: '#0b0b14',
  physics: {
    default: 'arcade',
    arcade: { debug: false },
  },
  scene: [BootScene, ArenaScene],
});

// PSV-1911 QA — webviews lie about every viewport metric at module-eval
// time (two live repros: 4:3 square, then an oversized canvas). Once the
// page has actually laid out, the #game rect IS ground truth — re-derive
// the internal width from it until the arena scene starts (after that
// the HUD is anchored and the size must freeze).
function syncAspectFromLayout(): void {
  if (game.scene.isActive('ArenaScene')) return;
  const rect = document.getElementById('game')?.getBoundingClientRect();
  if (rect === undefined || rect.width < 50 || rect.height < 50) return;
  const aspect = Math.min(21 / 9, Math.max(4 / 3, rect.width / rect.height));
  const width = Math.round(GAME_HEIGHT * aspect);
  if (Math.abs(width - game.scale.gameSize.width) > 8) {
    // eslint-disable-next-line no-console
    console.info(
      `[viewport] correcting internal size ${game.scale.gameSize.width}x${GAME_HEIGHT} → ${width}x${GAME_HEIGHT} (layout ${Math.round(rect.width)}x${Math.round(rect.height)})`,
    );
    game.scale.setGameSize(width, GAME_HEIGHT);
    game.scale.refresh();
  }
}
window.addEventListener('DOMContentLoaded', () => setTimeout(syncAspectFromLayout, 50));
window.addEventListener('resize', syncAspectFromLayout);
window.visualViewport?.addEventListener('resize', syncAspectFromLayout);
setTimeout(syncAspectFromLayout, 400);
setTimeout(syncAspectFromLayout, 1200);

// `?debug-viewport` — paints raw metrics on screen (webview consoles are
// unreachable; this is how we diagnose the next lying browser).
if (new URLSearchParams(window.location.search).has('debug-viewport')) {
  const el = document.createElement('pre');
  el.style.cssText =
    'position:fixed;top:40px;left:8px;z-index:99999;color:#0f0;background:#000c;' +
    'font-size:11px;padding:6px;pointer-events:none';
  const dump = (): void => {
    const r = document.getElementById('game')?.getBoundingClientRect();
    el.textContent = [
      `inner ${window.innerWidth}x${window.innerHeight}`,
      `vv ${window.visualViewport?.width ?? '-'}x${window.visualViewport?.height ?? '-'}`,
      `screen ${window.screen.width}x${window.screen.height} dpr ${window.devicePixelRatio}`,
      `docEl ${document.documentElement.clientWidth}x${document.documentElement.clientHeight}`,
      `#game ${Math.round(r?.width ?? 0)}x${Math.round(r?.height ?? 0)}`,
      `internal ${game.scale.gameSize.width}x${game.scale.gameSize.height}`,
    ].join('\n');
  };
  setInterval(dump, 500);
  document.body.appendChild(el);
}

if (import.meta.env.DEV) {
  // Console-driven verification (`client.session.state`, `getMatchConfig()`,
  // `getPlayer()`) per PSV-1335 acceptance.
  Object.assign(globalThis as Record<string, unknown>, {
    client,
    getMatchConfig,
    getPlayer,
  });
}
